// Copyright (c) Ping. All rights reserved.
//
// Chat-list preview formatter — produces the one-line text rendered
// inside `ConversationRowInner.lastMessage.preview` for every
// renderable AppEvent kind. Mirrors the matrix in
// `/tmp/ping-figma-tiles/SYNTHESIS.md` "ConversationRowInner —
// preview formatter coverage" so the chat-list subtitle is always
// non-empty for known kinds and falls back to "Unrenderable message"
// for forward-compat events.

import { parseEnvelope, type ParseResult } from './envelope';
import { formatBytes, formatDuration } from '../chat/messageFormatters';
import type { ForwardBody, TextBody } from './types';

/** Soft cap on the preview text we persist on the conversation row. */
export const PREVIEW_MAX_CHARS = 120;

/**
 * Truncates + whitespace-collapses a candidate preview. Returns
 * `undefined` if the result is empty so Dexie `update()` calls can
 * skip writing the field.
 */
function truncate(s: string | undefined): string | undefined {
  if (s === undefined) return undefined;
  const collapsed = s.replace(/[\r\n\t]+/g, ' ').trim();
  if (collapsed.length === 0) return undefined;
  if (collapsed.length <= PREVIEW_MAX_CHARS) return collapsed;
  return `${collapsed.slice(0, PREVIEW_MAX_CHARS - 1)}…`;
}

/**
 * Inline a mention list into the raw text so the chat-list preview
 * surfaces "@Alice" pills as plain text. The doc range-based shape
 * lets us substitute the canonical display name even if the original
 * body used a placeholder character.
 */
function inlineMentions(body: TextBody): string {
  if (!body.mentions || body.mentions.length === 0) return body.body;
  const sorted = [...body.mentions].sort(
    (a, b) => b.range.start - a.range.start,
  );
  let out = body.body;
  for (const m of sorted) {
    const name = m.displayName ?? 'user';
    out = `${out.slice(0, m.range.start)}@${name}${out.slice(m.range.end)}`;
  }
  return out;
}

// Exported so the UI (`PreviewLabel`) can map a preview string's leading glyph
// to a lucide icon — the text form stays authoritative for OS notifications /
// search / any plain-text context; the visual tiles swap the glyph for an icon.
const TEXT_GLYPH = '';
export const PHOTO_GLYPH = '📷';
export const VIDEO_GLYPH = '🎥';
export const VOICE_GLYPH = '🎤';
export const FILE_GLYPH = '📎';
export const LINK_GLYPH = '🔗';
export const STICKER_GLYPH = '🪄';
export const GIF_GLYPH = '🎞️';
export const LOCATION_GLYPH = '📍';
export const CALL_AUDIO_GLYPH = '📞';
export const CALL_VIDEO_GLYPH = '📹';
export const MEETING_GLYPH = '📅';
export const POLL_GLYPH = '📊';
export const PIN_GLYPH = '📌';
export const CODE_GLYPH = '‹›';

function prefix(glyph: string, text: string): string {
  if (glyph.length === 0) return text;
  return `${glyph} ${text}`;
}

/**
 * Collapse Markdown in a text-message body into a clean one-line preview so
 * the chat list / reply chips never show raw `**bold**`, ``` fences, or
 * `[label](url)`. Code blocks become a `‹› <first code line>` token, inline
 * code loses its backticks, links reduce to their label, and emphasis /
 * heading / quote markers are stripped. (Purely presentational — the stored
 * message body is untouched.)
 */
function stripMarkdownForPreview(text: string): string {
  let s = text;
  // Fenced code block → glyph + first non-empty (dedented) code line.
  s = s.replace(
    /```[a-zA-Z0-9+#.\-]*\r?\n?([\s\S]*?)```/g,
    (_m, body: string) => {
      const first = body
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l.length > 0);
      return `${CODE_GLYPH} ${first ?? 'Code'}`;
    },
  );
  // Stray lone fence markers (unterminated).
  s = s.replace(/```[a-zA-Z0-9+#.\-]*/g, '');
  // Links → label (or the URL when unlabelled).
  s = s.replace(/\[([^\]]*)\]\(([^)\s]+)\)/g, (_m, label: string, url: string) =>
    label.length > 0 ? label : url,
  );
  // Inline code + emphasis markers.
  s = s.replace(/`([^`]+)`/g, '$1');
  s = s.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1');
  s = s.replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1$2');
  // Single-underscore italic — flanked so intraword `snake_case` survives.
  s = s.replace(/(^|[\s([{])_([^_\s][^_]*?)_(?=[\s.,!?)\]}]|$)/g, '$1$2');
  s = s.replace(/~~([^~]+)~~/g, '$1');
  // Leading heading / blockquote markers.
  s = s.replace(/^\s{0,3}#{1,6}\s+/gm, '').replace(/^\s{0,3}>\s?/gm, '');
  return s;
}

/**
 * Core formatter — takes a parsed envelope and returns the preview
 * text. Sidecar / unrenderable kinds return `undefined` so the
 * caller can skip writing the chat-list preview field for them.
 */
export function formatParsedPreview(parsed: ParseResult): string | undefined {
  if (parsed.kind === 'unrenderable') {
    return 'Unrenderable message';
  }
  // Sidecars (delivered / read / profile) never produce a preview.
  if (
    parsed.kind === 'com.ping.delivered' ||
    parsed.kind === 'com.ping.read' ||
    parsed.kind === 'ping.profile'
  ) {
    return undefined;
  }

  switch (parsed.kind) {
    case 'com.ping.text': {
      const t = parsed.body as TextBody;
      return truncate(
        prefix(TEXT_GLYPH, stripMarkdownForPreview(inlineMentions(t))),
      );
    }
    case 'com.ping.image': {
      const b = parsed.body as { caption?: string };
      const caption = b.caption?.trim();
      return truncate(
        caption && caption.length > 0
          ? `${PHOTO_GLYPH} Photo · ${caption}`
          : `${PHOTO_GLYPH} Photo`,
      );
    }
    case 'com.ping.video': {
      const b = parsed.body as { caption?: string };
      const caption = b.caption?.trim();
      return truncate(
        caption && caption.length > 0
          ? `${VIDEO_GLYPH} Video · ${caption}`
          : `${VIDEO_GLYPH} Video`,
      );
    }
    case 'com.ping.voice': {
      const b = parsed.body as { dur_ms: number };
      return truncate(`${VOICE_GLYPH} Voice message · ${formatDuration(b.dur_ms)}`);
    }
    case 'com.ping.file': {
      const b = parsed.body as { name: string; size: number };
      const name = b.name.trim().length > 0 ? b.name : 'File';
      const size = b.size > 0 ? ` · ${formatBytes(b.size)}` : '';
      return truncate(`${FILE_GLYPH} ${name}${size}`);
    }
    case 'com.ping.location': {
      const b = parsed.body as { label?: string };
      const label = b.label?.trim();
      return truncate(
        label && label.length > 0
          ? `${LOCATION_GLYPH} ${label}`
          : `${LOCATION_GLYPH} Location`,
      );
    }
    case 'com.ping.sticker':
      return truncate(`${STICKER_GLYPH} Sticker`);
    case 'com.ping.gif':
      return truncate(`${GIF_GLYPH} GIF`);
    case 'com.ping.link_preview': {
      const b = parsed.body as { title: string; url: string };
      const text = b.title.trim().length > 0 ? b.title : b.url;
      return truncate(`${LINK_GLYPH} ${text}`);
    }
    case 'com.ping.edit': {
      const b = parsed.body as { new_text?: string };
      return truncate(b.new_text);
    }
    case 'com.ping.redact':
      return 'Message deleted';
    case 'com.ping.forward': {
      // Recurse on the inner content if it was decoded eagerly. The
      // doc lets `content_body` be raw CBOR bytes; the JSON wrapper
      // today nests the decoded payload directly, so we'd see an
      // object — try parsing as an envelope first for robustness.
      const f = parsed.body as ForwardBody;
      const inner = f.content_body;
      const innerKind = pickKindForType(f.content_type);
      if (innerKind === undefined) {
        return truncate('Forwarded message');
      }
      // Synthesize a minimal `ParseResult` for the inner content so
      // the recursion shares all glyph rules above.
      let innerPayload: Record<string, unknown> = {};
      if (inner && typeof inner === 'object') {
        innerPayload = inner as Record<string, unknown>;
      } else if (typeof inner === 'string') {
        const parsedInner = parseEnvelope(inner);
        if (parsedInner) {
          const innerPreview = formatParsedPreview(parsedInner);
          if (innerPreview !== undefined) {
            return truncate(`Forwarded: ${innerPreview}`);
          }
        }
        innerPayload = { body: inner };
      }
      // Build a synthetic envelope shape — kind = innerKind, payload =
      // innerPayload. Pass through `parseEnvelope` so the body-decoder
      // logic normalizes the shape.
      const synthetic = parseEnvelope(
        JSON.stringify({ kind: innerKind, payload: innerPayload }),
      );
      if (synthetic) {
        const innerPreview = formatParsedPreview(synthetic);
        if (innerPreview !== undefined) {
          return truncate(`Forwarded: ${innerPreview}`);
        }
      }
      return truncate('Forwarded message');
    }
    case 'com.ping.reaction':
    case 'com.ping.reaction_remove':
      // Reactions intentionally don't bump the chat-list preview —
      // the prior text stays visible. Caller treats `undefined` as
      // "don't overwrite".
      return undefined;
    case 'com.ping.thread_root':
      return undefined;
    case 'com.ping.group_name': {
      const b = parsed.body as { name: string; actorDisplayName?: string };
      const actor = b.actorDisplayName ?? 'Someone';
      return truncate(`${actor} renamed the group to "${b.name}"`);
    }
    case 'com.ping.group_avatar': {
      const b = parsed.body as { actorDisplayName?: string };
      const actor = b.actorDisplayName ?? 'Someone';
      return truncate(`${actor} updated the group icon`);
    }
    case 'com.ping.group_topic': {
      const b = parsed.body as { topic: string; actorDisplayName?: string };
      const actor = b.actorDisplayName ?? 'Someone';
      return truncate(`${actor} changed the topic to "${b.topic}"`);
    }
    case 'com.ping.invite': {
      const b = parsed.body as {
        actorDisplayName?: string;
        inviteeDisplayName?: string;
      };
      const actor = b.actorDisplayName ?? 'Someone';
      const invitee = b.inviteeDisplayName ?? 'a new member';
      return truncate(`${actor} invited ${invitee}`);
    }
    case 'com.ping.kick': {
      const b = parsed.body as {
        actorDisplayName?: string;
        targetDisplayName?: string;
        reason?: string;
      };
      const actor = b.actorDisplayName ?? 'Someone';
      if (b.reason === 'left') return truncate(`${actor} left`);
      const target = b.targetDisplayName ?? 'a member';
      return truncate(`${actor} removed ${target}`);
    }
    case 'com.ping.pin': {
      const b = parsed.body as { actorDisplayName?: string };
      const actor = b.actorDisplayName ?? 'Someone';
      return truncate(`${PIN_GLYPH} ${actor} pinned a message`);
    }
    case 'com.ping.unpin': {
      const b = parsed.body as { actorDisplayName?: string };
      const actor = b.actorDisplayName ?? 'Someone';
      return truncate(`${actor} unpinned a message`);
    }
    case 'com.ping.poll': {
      const b = parsed.body as { question: string };
      return truncate(`${POLL_GLYPH} Poll: ${b.question}`);
    }
    case 'com.ping.call_invite': {
      const b = parsed.body as { media: 'audio' | 'video'; group?: boolean };
      const glyph = b.media === 'video' ? CALL_VIDEO_GLYPH : CALL_AUDIO_GLYPH;
      // Group (huddle) call → distinct copy so the list row reads "Group call"
      // instead of a 1:1 "Audio/Video call".
      const label =
        b.group === true
          ? b.media === 'video'
            ? 'Group video call'
            : 'Group call'
          : b.media === 'video'
            ? 'Video call'
            : 'Audio call';
      return truncate(`${glyph} ${label}`);
    }
    case 'com.ping.call_end': {
      const b = parsed.body as { duration_ms?: number };
      if (b.duration_ms !== undefined && b.duration_ms > 0) {
        return truncate(`${CALL_AUDIO_GLYPH} Call ended · ${formatDuration(b.duration_ms)}`);
      }
      return truncate(`${CALL_AUDIO_GLYPH} Call ended`);
    }
    case 'com.ping.meeting': {
      const b = parsed.body as { title: string };
      return truncate(`${MEETING_GLYPH} Meeting: ${b.title}`);
    }
    // Meeting update/rsvp/cancel are sidecars — they mutate the meeting card in
    // place and never bump the chat-list preview (byte-for-byte iOS parity).
    case 'com.ping.meeting_update':
    case 'com.ping.meeting_rsvp':
    case 'com.ping.meeting_cancel':
      return undefined;
    case 'com.ping.game_create': {
      const b = parsed.body as { game_kind?: string };
      const labels: Record<string, string> = {
        coinflip: 'Coin Flip',
        dice: 'Dice Roll',
        rps: 'Rock Paper Scissors',
        chess: 'Chess',
        blackjack: 'Blackjack',
        racing: 'Racing',
      };
      const name = (b.game_kind && labels[b.game_kind]) || 'Game';
      return truncate(`🎮 ${name}`);
    }
    case 'com.ping.game_move':
    case 'com.ping.game_outcome':
    case 'com.ping.game_join':
    case 'com.ping.game_reveal':
      return truncate('🎮 Game update');
    // Live-call signaling never shows in the chat-list preview — it
    // drives the call UI, not the message log. calls.md §5.3.10.
    case 'com.ping.call_accept':
    case 'com.ping.call_decline':
    case 'com.ping.call_ringing':
    case 'com.ping.call_hand_raise':
    case 'com.ping.call_reaction':
    case 'com.ping.call_roster':
    case 'com.ping.call_handoff':
      return undefined;
  }
}

/**
 * Top-level entry: takes whatever the receiver got off the wire —
 * raw bytes, the persisted body string, or `undefined` — and returns
 * the chat-list preview. Returns `undefined` when the input collapses
 * to empty so callers can omit the field on conv-row updates.
 *
 * Backwards-compatible with the old `lastMessagePreview(body: string)`
 * call sites: plain text strings flow through `parseEnvelope`'s
 * legacy-plain-text fast-path and produce the same output.
 */
export function formatMessagePreview(
  input: Uint8Array | string | undefined | null,
): string | undefined {
  const parsed = parseEnvelope(input);
  if (parsed === undefined) return undefined;
  return formatParsedPreview(parsed);
}

// Minimal type-tag → kind lookup for the forwarded-content branch.
// Kept local so `formatPreview.ts` doesn't pull the whole `kinds.ts`
// reverse map onto the public API.
function pickKindForType(type: number): string | undefined {
  switch (type) {
    case 0x10:
      return 'com.ping.text';
    case 0x11:
      return 'com.ping.image';
    case 0x12:
      return 'com.ping.video';
    case 0x13:
      return 'com.ping.voice';
    case 0x14:
      return 'com.ping.file';
    case 0x15:
      return 'com.ping.location';
    case 0x19:
      return 'com.ping.sticker';
    case 0x1a:
      return 'com.ping.link_preview';
    default:
      return undefined;
  }
}
